import React, { useState, useEffect, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Upload, 
  FileText, 
  DollarSign, 
  TrendingUp, 
  Download, 
  Calculator,
  PieChart,
  Receipt,
  BarChart3,
  FileSpreadsheet,
  User,
  Settings,
  LogOut,
  Crown,
  Sparkles,
  Layers,
  X,
  Shield,
  Menu,
  Clock,
  CheckCircle,
  CreditCard,
  Users,
  Loader2,
  ChevronDown,
  ChevronUp,
  ArrowLeftRight,
  Combine
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from "recharts";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

function Dashboard() {
  const { user, logout, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [uploadedStatement, setUploadedStatement] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedBank, setSelectedBank] = useState("regions_bank");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [notBankStatement, setNotBankStatement] = useState(false);
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [mergedCount, setMergedCount] = useState(0);
  const [grandCount, setGrandCount] = useState(0);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Filter transactions by category
  const getTransactionsByCategory = (category) => {
    if (!uploadedStatement || !uploadedStatement.transactions) return [];
    return uploadedStatement.transactions.filter(tx => 
      tx.category === category && tx.is_business_expense
    );
  };

  // Sequential bulk-upload queue. Each entry tracks per-file progress so the
  // user sees a live picture of which file is uploading vs. queued vs. done.
  const [uploadQueue, setUploadQueue] = useState([]);

  const updateQueueEntry = (id, patch) => {
    setUploadQueue(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  };

  const processOneFile = async (entry) => {
    const file = entry.file;
    updateQueueEntry(entry.id, { status: 'uploading', error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bank_format', selectedBank);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tax-helper/upload-statement`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      });
      updateQueueEntry(entry.id, { status: 'done', result: response.data });
      return { ok: true, data: response.data };
    } catch (error) {
      let detail = "Failed to process";
      if (error.response?.status === 401) {
        detail = "Authentication required";
        logout(); navigate('/auth');
      } else if (error.response?.status === 402) {
        detail = "Insufficient credits";
      } else if (error.response?.status === 422) {
        detail = "Not a bank statement";
      } else if (error.response?.data?.detail) {
        detail = error.response.data.detail;
      }
      updateQueueEntry(entry.id, { status: 'error', error: detail });
      return { ok: false, error, detail };
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const pdfs = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) {
      toast.error("Please upload PDF file(s)");
      return;
    }
    if (pdfs.length < acceptedFiles.length) {
      toast.warning(`Skipped ${acceptedFiles.length - pdfs.length} non-PDF file(s)`);
    }
    if (user?.credit_balance < 2.0) {
      toast.error("Insufficient credits. Please purchase more credits to process PDFs.");
      navigate('/subscription');
      return;
    }

    // Single-file path: preserve existing single-file detail view behavior.
    if (pdfs.length === 1) {
      const file = pdfs[0];
      setIsUploading(true);
      const entry = { id: `${Date.now()}-0`, file, status: 'uploading', error: null, result: null };
      setUploadQueue([entry]);
      const res = await processOneFile(entry);
      if (res.ok) {
        setUploadedStatement(res.data);
        toast.success("Bank statement processed successfully!");
        try {
          const token = localStorage.getItem('token');
          const tx = await axios.get(`${API}/tax-helper/tax-summary/${res.data.id}`, { headers: { Authorization: `Bearer ${token}` } });
          setTaxSummary(tx.data);
        } catch (e) { toast.warning("Tax summary failed to load; available later."); }
        try { await refreshUser(); } catch (e) { /* ignore */ }
        try {
          const token = localStorage.getItem('token');
          const list = await axios.get(`${API}/tax-helper/statements`, { headers: { Authorization: `Bearer ${token}` } });
          setStatements(list.data || []);
        } catch (e) { /* ignore */ }
      } else {
        toast.error(res.detail);
        if (res.error?.response?.status === 422) setNotBankStatement(true);
        if (res.error?.response?.status === 402) navigate('/subscription');
      }
      setIsUploading(false);
      return;
    }

    // Bulk path: queue + sequential processing with per-file status.
    setIsUploading(true);
    const initial = pdfs.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      file: f,
      status: 'queued',
      error: null,
      result: null,
    }));
    setUploadQueue(initial);
    let okCount = 0;
    let failCount = 0;
    for (const entry of initial) {
      const res = await processOneFile(entry);
      if (res.ok) okCount++;
      else {
        failCount++;
        // Stop the queue immediately on credit / auth failures
        if (res.error?.response?.status === 402) {
          toast.error("Out of credits — remaining uploads cancelled");
          break;
        }
        if (res.error?.response?.status === 401) {
          break;
        }
      }
    }
    try { await refreshUser(); } catch (e) { /* ignore */ }
    try {
      const token = localStorage.getItem('token');
      const list = await axios.get(`${API}/tax-helper/statements`, { headers: { Authorization: `Bearer ${token}` } });
      setStatements(list.data || []);
    } catch (e) { /* ignore */ }
    if (okCount > 0) toast.success(`Processed ${okCount} statement${okCount === 1 ? '' : 's'}`);
    if (failCount > 0) toast.error(`${failCount} file${failCount === 1 ? '' : 's'} failed — see queue`);
    setIsUploading(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
    disabled: isUploading
  });

  const loadStatements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tax-helper/statements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setStatements(response.data || []);
    } catch (error) {
      console.error('Error loading statements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (statementId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tax-helper/export-excel/${statementId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax_analysis_${statementId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export Excel file");
    }
  };

  const handleExportGoogleSheets = (statementId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Please log in to export to Google Sheets");
      return;
    }
    // Open the Google Sheets OAuth flow in a new tab
    const authUrl = `${API}/tax-helper/google-sheets/auth/${statementId}?token=${token}`;
    window.open(authUrl, '_blank');
    toast.success("Opening Google Sheets... Please authorize if prompted.");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const prepareChartData = () => {
    if (!taxSummary) return [];
    
    return Object.entries(taxSummary.expense_categories).map(([category, amount]) => ({
      category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      amount: amount,
      percentage: ((amount / taxSummary.total_business_expenses) * 100).toFixed(1)
    }));
  };

  const preparePieData = () => {
    if (!taxSummary) return [];
    
    return Object.entries(taxSummary.expense_categories)
      .map(([category, amount]) => ({
        name: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: amount
      }))
      .slice(0, 6); // Top 6 categories
  };

  useEffect(() => {
    // Load statements when user is available (but don't reload on user credit updates)
    if (user && user.id) {
      console.log('Loading statements for user:', user.id);
      loadStatements();

      // Fetch suggestion count for the My Rules nav badge
      const token = localStorage.getItem('token');
      axios.get(`${API}/tax-helper/user-rules/suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        setSuggestionCount((res.data?.suggestions || []).length);
      }).catch(() => { /* silent */ });

      axios.get(`${API}/tax-helper/merged-statements`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        setMergedCount((res.data?.merged_statements || []).length);
      }).catch(() => { /* silent */ });

      axios.get(`${API}/tax-helper/grand-statements`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        setGrandCount((res.data?.grand_statements || []).length);
      }).catch(() => { /* silent */ });
    }
  }, [user?.id]); // Only depend on user ID, not the entire user object

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="text-sm">
                  <div className="font-medium text-slate-800">{user?.username}</div>
                  <div className="text-slate-500 text-xs">
                    <div className="flex items-center">
                      <CreditCard className="w-3 h-3 mr-1 text-green-500" />
                      ${user?.credit_balance?.toFixed(2) || '0.00'} Credits
                    </div>
                  </div>
                </div>
              </div>
              
              {user?.credit_balance < 2.0 && (
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Buy Credits
                </Button>
              )}
              
              <Button 
                variant="outline"
                onClick={() => navigate('/previous-statements')}
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Previous Statements
              </Button>

              {user?.is_admin && (
                <Button 
                  variant="outline"
                  onClick={() => navigate('/admin')}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => navigate('/my-rules')}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 relative"
                data-testid="my-rules-nav-dashboard"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                My Rules
                {suggestionCount > 0 && (
                  <span
                    className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-xs font-semibold leading-none"
                    data-testid="my-rules-badge-dashboard"
                    title={`${suggestionCount} suggested rule${suggestionCount === 1 ? '' : 's'} ready to review`}
                  >
                    {suggestionCount}
                  </span>
                )}
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              >
                <Menu className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden mt-4 pt-4 border-t border-slate-200">
              <div className="space-y-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-800">{user?.username}</div>
                  <div className="text-slate-500 text-xs">
                    <div className="flex items-center">
                      <CreditCard className="w-3 h-3 mr-1 text-green-500" />
                      ${user?.credit_balance?.toFixed(2) || '0.00'} Credits
                    </div>
                  </div>
                </div>
                
                {user?.credit_balance < 2.0 && (
                  <Button 
                    onClick={() => navigate('/subscription')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Buy Credits
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/previous-statements')}
                  className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Previous Statements
                </Button>
                
                {user?.is_admin && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/admin')}
                    className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Button>
                )}
                
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="w-full text-slate-600 hover:text-slate-800"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Welcome to Tax Helper Pros
          </h1>
          <p className="text-xl text-slate-600">
            Pay-per-use tax processing - Upload PDFs and pay only for what you process
          </p>
        </div>

        {/* Credit Balance Banner */}
        <Card className="mb-8 border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-center md:text-left mb-4 md:mb-0">
                <h3 className="text-2xl font-semibold text-blue-800 mb-2">
                  Credit Balance: ${user?.credit_balance?.toFixed(2) || '0.00'}
                </h3>
                <p className="text-blue-700">
                  Processing costs: $1 per PDF upload + $1 per page
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {user?.credit_balance > 0 ? (
                  <Badge className="bg-green-600 text-white px-4 py-2">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Ready to Process
                  </Badge>
                ) : (
                  <Badge className="bg-orange-600 text-white px-4 py-2">
                    <Clock className="w-4 h-4 mr-2" />
                    Need Credits
                  </Badge>
                )}
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Buy Credits
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Section */}
        {!uploadedStatement && !notBankStatement && (
          <Card className={`mb-8 border-2 border-dashed transition-colors ${
            (user?.credit_balance || 0) >= 2.0
              ? "border-slate-300 hover:border-blue-400" 
              : "border-gray-200 bg-gray-50"
          }`}>
            <CardContent className="p-8">
              {/* Bank Selection Dropdown */}
              <div className="mb-6 max-w-md mx-auto">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Bank
                </label>
                <Select value={selectedBank} onValueChange={setSelectedBank}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regions_bank">Regions Bank</SelectItem>
                    <SelectItem value="wells_fargo">Wells Fargo Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div {...getRootProps()} className={`cursor-pointer ${
                (user?.credit_balance || 0) < 2.0 ? 'cursor-not-allowed opacity-60' : ''
              }`}>
                <input {...getInputProps()} data-testid="pdf-upload-input" disabled={(user?.credit_balance || 0) < 2.0} />
                <div className="text-center">
                  {isDragActive ? (
                    <div className="space-y-4">
                      <Upload className="w-16 h-16 mx-auto text-blue-500" />
                      <p className="text-lg font-medium text-blue-600">Drop your PDF here...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {isUploading ? (
                        /* Enhanced Processing UI */
                        <div className="space-y-6">
                          {/* Animated Spinner Badge */}
                          <div className="flex justify-center">
                            <div className="relative">
                              {/* Outer pulsing ring */}
                              <div className="absolute inset-0 bg-blue-400 rounded-full animate-ping opacity-75"></div>
                              {/* Main spinner circle */}
                              <div className="relative bg-gradient-to-br from-blue-500 to-blue-600 rounded-full p-6 shadow-lg">
                                <Loader2 className="w-12 h-12 text-white animate-spin" />
                              </div>
                            </div>
                          </div>
                          
                          {/* Processing Text */}
                          <div className="space-y-2">
                            <h3 className="text-xl font-bold text-slate-800">
                              Processing Your Bank Statement
                            </h3>
                            <p className="text-sm text-slate-600">
                              Our AI is analyzing your transactions and categorizing expenses...
                            </p>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="w-full max-w-md mx-auto space-y-2">
                            <Progress value={66} className="w-full h-2" />
                            <div className="flex justify-between text-xs text-slate-500">
                              <span>Reading PDF</span>
                              <span>Categorizing</span>
                              <span>Analyzing</span>
                            </div>
                          </div>
                          
                          {/* Processing Steps */}
                          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto pt-4">
                            <div className="space-y-2">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                              </div>
                              <p className="text-xs font-medium text-slate-700">PDF Uploaded</p>
                            </div>
                            <div className="space-y-2">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                              </div>
                              <p className="text-xs font-medium text-blue-700">Processing...</p>
                            </div>
                            <div className="space-y-2">
                              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                                <Calculator className="w-6 h-6 text-slate-400" />
                              </div>
                              <p className="text-xs font-medium text-slate-500">Finalizing</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Default Upload UI */
                        <>
                          <FileText className="w-16 h-16 mx-auto text-slate-400" />
                          <div>
                            <p className="text-lg font-medium text-slate-700">
                              {(user?.credit_balance || 0) >= 2.0
                                ? "Upload your PDF bank statement"
                                : "Purchase credits to upload bank statements"
                              }
                            </p>
                            <p className="text-sm text-slate-500 mt-2">
                              {(user?.credit_balance || 0) >= 2.0
                                ? "Drag and drop one or more PDFs (bulk upload supported) or click to select"
                                : "You need at least $2.00 in credits to process a PDF file"
                              }
                            </p>
                          </div>
                          {(user?.credit_balance || 0) < 2.0 && (
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/subscription');
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              Buy Credits
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk upload queue panel */}
        {uploadQueue.length > 0 && uploadQueue.length > 1 && (
          <Card className="mb-8" data-testid="bulk-upload-queue">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Bulk upload queue ({uploadQueue.filter(e => e.status === 'done').length}/{uploadQueue.length} done)
              </CardTitle>
              {isUploading && (
                <CardDescription>
                  Processing one file at a time. You'll see results below when finished.
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {uploadQueue.map(entry => {
                  const statusStyles = {
                    queued: 'bg-slate-100 text-slate-600',
                    uploading: 'bg-blue-100 text-blue-700',
                    done: 'bg-emerald-100 text-emerald-700',
                    error: 'bg-rose-100 text-rose-700',
                  };
                  const statusLabel = {
                    queued: 'Queued',
                    uploading: 'Uploading…',
                    done: 'Done',
                    error: 'Failed',
                  };
                  return (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between gap-3 p-2.5 rounded border border-slate-200 bg-white"
                      data-testid={`queue-entry-${entry.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {entry.status === 'uploading' && <Loader2 className="w-4 h-4 text-blue-600 animate-spin shrink-0" />}
                        {entry.status === 'done' && <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />}
                        {entry.status === 'error' && <X className="w-4 h-4 text-rose-600 shrink-0" />}
                        {entry.status === 'queued' && <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />}
                        <span className="text-sm text-slate-800 truncate" title={entry.file.name}>{entry.file.name}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {entry.status === 'error' && entry.error && (
                          <span className="text-xs text-rose-600 max-w-[200px] truncate" title={entry.error}>{entry.error}</span>
                        )}
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyles[entry.status]}`}>
                          {statusLabel[entry.status]}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!isUploading && uploadQueue.some(e => e.status === 'done') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => { setUploadQueue([]); navigate('/previous-statements'); }}
                  data-testid="view-uploaded-statements-btn"
                >
                  View processed statements
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Not a Bank Statement Error */}
        {notBankStatement && (
          <Card className="mb-8 border-2 border-red-200 bg-red-50" data-testid="not-bank-statement-error">
            <CardContent className="p-8 text-center">
              <FileText className="w-14 h-14 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800 mb-2">
                This PDF Does Not Appear to Be a Bank Statement
              </h3>
              <p className="text-slate-600 mb-6 max-w-lg mx-auto">
                The uploaded document doesn't contain the expected bank statement structure 
                (account numbers, balances, transactions). Please verify you're uploading the 
                correct file and try again.
              </p>
              <Button
                onClick={() => setNotBankStatement(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="try-again-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        {!uploadedStatement && statements.length > 0 && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="text-center">
                <FileSpreadsheet className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">View Previous Statements</h3>
                <p className="text-slate-600 mb-4">
                  You have {statements.length} processed statement{statements.length !== 1 ? 's' : ''} ready to view
                </p>
                <Button 
                  onClick={() => navigate('/previous-statements')}
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  View Previous Statements
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Merged / Year-to-Date Statements Tile */}
        {!uploadedStatement && mergedCount > 0 && (
          <Card className="mb-8 border-emerald-200">
            <CardContent className="p-6">
              <div className="text-center">
                <Layers className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">View Year-to-Date Statements</h3>
                <p className="text-slate-600 mb-4">
                  You have {mergedCount} processed merged statement{mergedCount !== 1 ? 's' : ''} ready to view
                </p>
                <Button
                  onClick={() => navigate('/merged-statements')}
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="view-merged-statements"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  View Merged Statements
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grand Statements Tile (merges of multiple YTDs) */}
        {!uploadedStatement && grandCount > 0 && (
          <Card className="mb-8 border-purple-200">
            <CardContent className="p-6">
              <div className="text-center">
                <Combine className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">View Grand Statements</h3>
                <p className="text-slate-600 mb-4">
                  You have {grandCount} multi-year Grand Statement{grandCount !== 1 ? 's' : ''} ready to view
                </p>
                <Button
                  onClick={() => navigate('/grand-statements')}
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  data-testid="view-grand-statements"
                >
                  <Combine className="w-4 h-4 mr-2" />
                  View Grand Statements
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Section */}
        {uploadedStatement && !taxSummary && (
          <Card className="mb-8 border-2 border-amber-200 bg-amber-50" data-testid="tax-summary-error">
            <CardContent className="p-8 text-center">
              <Calculator className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Statement Uploaded Successfully
              </h3>
              <p className="text-slate-600 mb-6">
                Your statement was processed, but the tax summary could not be loaded. You can view it from your Previous Statements.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => {
                    setUploadedStatement(null);
                    setTaxSummary(null);
                  }}
                  variant="outline"
                >
                  Upload Another Statement
                </Button>
                <Button
                  onClick={() => navigate('/previous-statements')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  View Previous Statements
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {uploadedStatement && taxSummary && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadedStatement(null);
                  setTaxSummary(null);
                }}
                data-testid="new-statement-btn"
              >
                Process New Statement
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleExportExcel(uploadedStatement.id)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button
                  onClick={() => handleExportGoogleSheets(uploadedStatement.id)}
                  variant="outline"
                  className="border-green-600 text-green-700 hover:bg-green-50"
                  data-testid="export-google-sheets-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Google Sheets
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Income</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600" data-testid="total-income">
                    {formatCurrency(taxSummary.total_income)}
                  </div>
                  <p className="text-xs text-muted-foreground">From deposits</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Business Expenses</CardTitle>
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600" data-testid="total-expenses">
                    {formatCurrency(taxSummary.total_business_expenses)}
                  </div>
                  <p className="text-xs text-muted-foreground">Deductible expenses</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600" data-testid="net-profit">
                    {formatCurrency(taxSummary.total_income - taxSummary.total_business_expenses)}
                  </div>
                  <p className="text-xs text-muted-foreground">Taxable income</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
                  <Calculator className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="total-transactions">
                    {uploadedStatement.transactions?.length || 0}
                  </div>
                  <p className="text-xs text-muted-foreground">Processed items</p>
                </CardContent>
              </Card>
            </div>

            {/* Balance and Transaction Summaries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5" />
                    Balance Summary
                  </CardTitle>
                  <CardDescription>Account balance information</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="font-medium text-blue-800">Beginning Balance</span>
                      <span className="font-bold text-blue-600">
                        {formatCurrency(uploadedStatement.beginning_balance || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="font-medium text-purple-800">Ending Balance</span>
                      <span className="font-bold text-purple-600">
                        {formatCurrency(uploadedStatement.ending_balance || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border-t-2">
                      <span className="font-medium text-slate-800">Balance Change</span>
                      <span className={`font-bold ${
                        (uploadedStatement.ending_balance || 0) >= (uploadedStatement.beginning_balance || 0) 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {formatCurrency((uploadedStatement.ending_balance || 0) - (uploadedStatement.beginning_balance || 0))}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Transaction Value Summary
                  </CardTitle>
                  <CardDescription>Total dollar amounts by transaction type (all included in transaction count above)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Combined Deposits & Credits to match bank statement format */}
                    <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                      <div>
                        <span className="font-semibold text-green-900">Deposits & Credits</span>
                        <p className="text-xs text-green-700 mt-1">
                          Total from bank statement summary (business income + other credits)
                        </p>
                      </div>
                      <span className="font-bold text-2xl text-green-700">
                        {formatCurrency(uploadedStatement.deposits_credits || 
                          (uploadedStatement.deposits || 0) + (uploadedStatement.credits || 0))}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <div>
                        <span className="font-medium text-red-800">Withdrawals</span>
                        <p className="text-xs text-red-600">Total value of withdrawal transactions</p>
                      </div>
                      <span className="font-bold text-red-600">
                        {formatCurrency(uploadedStatement.withdrawals || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <div>
                        <span className="font-medium text-orange-800">Fees</span>
                        <p className="text-xs text-orange-600">Total value of fee transactions</p>
                      </div>
                      <span className="font-bold text-orange-600">
                        {formatCurrency(uploadedStatement.fees || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <div>
                        <span className="font-medium text-blue-800">Automatic Transfers</span>
                        <p className="text-xs text-blue-600">Total value of automatic transfer transactions</p>
                      </div>
                      <span className="font-bold text-blue-600">
                        {formatCurrency(uploadedStatement.automatic_transfers || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                      <div>
                        <span className="font-medium text-indigo-800">Checks</span>
                        <p className="text-xs text-indigo-600">Total value of check transactions</p>
                      </div>
                      <span className="font-bold text-indigo-600">
                        {formatCurrency(uploadedStatement.checks || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <div>
                        <span className="font-medium text-purple-800">Transfers</span>
                        <p className="text-xs text-purple-600">Internal account transfers (EB to/from Checking, Savings, etc.)</p>
                      </div>
                      <span className="font-bold text-purple-600">
                        {formatCurrency(uploadedStatement.transfers || 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Analysis */}
            <Tabs defaultValue="categories" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="categories">Categories</TabsTrigger>
                <TabsTrigger value="transfers">Transfers</TabsTrigger>
                <TabsTrigger value="profit-loss">P&L</TabsTrigger>
                <TabsTrigger value="schedule-c">Schedule C</TabsTrigger>
                <TabsTrigger value="charts">Charts</TabsTrigger>
                <TabsTrigger value="transactions">Transactions</TabsTrigger>
              </TabsList>

              {/* Categories Tab with Expandable Dropdowns */}
              <TabsContent value="categories" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Expense Categories Breakdown</CardTitle>
                    <CardDescription>Click on any category to see individual transactions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(taxSummary.expense_categories).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(taxSummary.expense_categories)
                          .sort(([,a], [,b]) => b - a)
                          .map(([category, amount]) => {
                            const transactions = getTransactionsByCategory(category);
                            const isExpanded = expandedCategories[category];
                            
                            return (
                              <div key={category} className="border rounded-lg overflow-hidden">
                                {/* Category Header - Clickable */}
                                <button
                                  onClick={() => toggleCategory(category)}
                                  className="w-full flex justify-between items-center p-4 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                                >
                                  <div className="flex items-center gap-3">
                                    {isExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-slate-600" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-slate-600" />
                                    )}
                                    <div className="text-left">
                                      <p className="font-semibold text-slate-900 capitalize">
                                        {category.replace(/_/g, ' ')}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                                      </p>
                                    </div>
                                  </div>
                                  <span className="font-bold text-red-600 text-lg">
                                    {formatCurrency(amount)}
                                  </span>
                                </button>
                                
                                {/* Expanded Transaction List */}
                                {isExpanded && (
                                  <div className="p-4 bg-white border-t">
                                    {transactions.length > 0 ? (
                                      <div className="space-y-2">
                                        {transactions.map((tx, idx) => (
                                          <div 
                                            key={idx}
                                            className="flex justify-between items-start p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                          >
                                            <div className="flex-1">
                                              <p className="font-medium text-slate-900">
                                                {tx.description}
                                              </p>
                                              <div className="flex gap-3 mt-1 text-xs text-slate-500">
                                                <span>{tx.date}</span>
                                                {tx.subcategory && (
                                                  <Badge variant="outline" className="text-xs">
                                                    {tx.subcategory.replace(/_/g, ' ')}
                                                  </Badge>
                                                )}
                                                {tx.schedule_c_line && (
                                                  <span className="text-blue-600">
                                                    Schedule C Line {tx.schedule_c_line}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            <span className="font-bold text-red-600 ml-4">
                                              {formatCurrency(tx.amount)}
                                            </span>
                                          </div>
                                        ))}
                                        
                                        {/* Category Total */}
                                        <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border-t-2 border-red-200 mt-3">
                                          <span className="font-semibold text-slate-700">
                                            Category Total
                                          </span>
                                          <span className="font-bold text-red-700 text-lg">
                                            {formatCurrency(amount)}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-center text-slate-500 py-4">
                                        No transactions found
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        No expense categories found
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Transfers Tab */}
              <TabsContent value="transfers" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Internal Account Transfers</CardTitle>
                    <CardDescription>Money movements between your business accounts (not income or expenses)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {uploadedStatement.transactions.filter(tx => tx.category === 'transfers').length > 0 ? (
                      <div className="space-y-3">
                        {uploadedStatement.transactions
                          .filter(tx => tx.category === 'transfers')
                          .map((tx, idx) => (
                            <div 
                              key={idx}
                              className="flex justify-between items-start p-4 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors"
                            >
                              <div className="flex-1">
                                <p className="font-semibold text-purple-900">
                                  {tx.description}
                                </p>
                                <div className="flex gap-3 mt-2 text-sm text-purple-700">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {tx.date}
                                  </span>
                                  {tx.subcategory && (
                                    <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                                      {tx.subcategory.replace(/_/g, ' ')}
                                    </Badge>
                                  )}
                                  <span className="text-purple-600 font-medium">
                                    Type: {tx.transaction_type}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 text-right">
                                <p className="font-bold text-lg text-purple-700">
                                  {formatCurrency(tx.amount)}
                                </p>
                                <p className="text-xs text-purple-600 mt-1">
                                  Internal Transfer
                                </p>
                              </div>
                            </div>
                          ))}
                        
                        {/* Total Transfers Summary */}
                        <div className="flex justify-between items-center p-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg border-2 border-purple-300 mt-4">
                          <div>
                            <p className="font-bold text-purple-900 text-lg">Total Transfers</p>
                            <p className="text-sm text-purple-700">Sum of all internal account movements</p>
                          </div>
                          <p className="text-2xl font-bold text-purple-700">
                            {formatCurrency(
                              uploadedStatement.transactions
                                .filter(tx => tx.category === 'transfers')
                                .reduce((sum, tx) => sum + tx.amount, 0)
                            )}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <ArrowLeftRight className="w-8 h-8 text-purple-400" />
                        </div>
                        <p className="text-slate-600 font-medium">No internal transfers found</p>
                        <p className="text-sm text-slate-500 mt-2">
                          EB to/from Checking, Savings, or Now Card transfers will appear here
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* P&L Tab */}
              <TabsContent value="profit-loss" className="space-y-6">
                {/* Net Profit/Loss Summary Card */}
                <Card className={`${
                  (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                    ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300' 
                    : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300'
                } border-2`}>
                  <CardHeader>
                    <CardTitle className={`text-xl ${
                      (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                        ? 'text-blue-700' 
                        : 'text-orange-700'
                    }`}>
                      Profit & Loss Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <p className="text-lg font-medium text-slate-700">Income</p>
                        <p className="text-lg font-bold text-green-700">
                          +{formatCurrency(taxSummary.total_income)}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-lg font-medium text-slate-700">Expenses</p>
                        <p className="text-lg font-bold text-red-700">
                          -{formatCurrency(taxSummary.total_business_expenses)}
                        </p>
                      </div>
                      <div className="border-t-2 border-slate-300 pt-4">
                        <div className="flex justify-between items-center">
                          <p className={`text-xl font-bold ${
                            (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                              ? 'text-blue-700' 
                              : 'text-orange-700'
                          }`}>
                            {(taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? 'Net Profit' : 'Net Loss'}
                          </p>
                          <p className={`text-3xl font-bold ${
                            (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                              ? 'text-blue-700' 
                              : 'text-orange-700'
                          }`}>
                            {(taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? '+' : '-'}
                            {formatCurrency(Math.abs(taxSummary.total_income - taxSummary.total_business_expenses))}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Expense Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-700">Expense Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(taxSummary.expense_categories).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(taxSummary.expense_categories)
                          .sort(([,a], [,b]) => b - a)
                          .map(([category, amount]) => {
                            const percentage = (amount / taxSummary.total_business_expenses * 100).toFixed(1);
                            return (
                              <div key={category} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-red-900 capitalize">
                                      {category.replace(/_/g, ' ')}
                                    </p>
                                    <Badge variant="outline" className="text-xs">
                                      {percentage}%
                                    </Badge>
                                  </div>
                                  <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                                    <div 
                                      className="bg-red-600 h-2 rounded-full" 
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                                <p className="text-lg font-bold text-red-700 ml-4">
                                  {formatCurrency(amount)}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        No expense categories found
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="schedule-c" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Schedule C Summary</CardTitle>
                    <CardDescription>Tax form ready breakdown by line item</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(taxSummary.schedule_c_categories).map(([line, amount]) => (
                        <div key={line} className="flex justify-between items-center p-3 border rounded-lg bg-slate-50">
                          <div>
                            <p className="font-medium">{line}</p>
                          </div>
                          <div className="font-bold">{formatCurrency(amount)}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="charts" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        Expense Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={prepareChartData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="category" 
                            angle={-45} 
                            textAnchor="end" 
                            height={80}
                            fontSize={12}
                          />
                          <YAxis />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Bar dataKey="amount" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Top Expense Categories
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={preparePieData()}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {preparePieData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="transactions" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>All Transactions</CardTitle>
                    <CardDescription>Detailed view of all processed transactions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="transactions-container">
                        {uploadedStatement.transactions.map((transaction, index) => (
                          <div key={index} className="flex justify-between items-center p-3 border rounded-lg text-sm">
                            <div className="flex-1">
                              <p className="font-medium">{transaction.description}</p>
                              <p className="text-slate-500">{transaction.date}</p>
                            </div>
                            <div className="text-center px-4">
                              <Badge variant={transaction.transaction_type === 'credit' ? 'default' : 'secondary'}>
                                {transaction.transaction_type}
                              </Badge>
                            </div>
                            <div className="text-center px-4">
                              {transaction.category && (
                                <Badge variant="outline">
                                  {transaction.category.replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${transaction.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                {transaction.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;